<template>
  <div class="history-dialog-overlay" v-if="visible">
    <div class="history-dialog">
      <!-- 关闭按钮 -->
      <img
        @click="handleClose"
        src="@/assets/aiTeacher/ic-close.png"
        alt="关闭"
        class="close-btn"
      />

      <!-- 内容区域 -->
      <div class="dialog-content" @scroll="handleScroll">
        <template v-if="historyList?.length > 0">
          <template
            v-for="(group, groupTitle, index) in groupedHistory"
            :key="groupTitle"
          >
            <div class="date-title" :class="{ act_line: index > 0 }">
              {{ groupTitle }}
            </div>
            <div class="history-items">
              <div
                v-for="item in group"
                :key="item.id"
                class="history-item"
                @click="handleSelect(item)"
              >
                <div
                  class="item-text"
                  :class="{ active: selectedId === item.id }"
                >
                  {{ item.title || item.type }}
                </div>
                <!-- <div class="delete-btn" @click.stop="handleDelete(item)">
                  <img
                    src="@/assets/aiTeacher/ic-del.png"
                    alt=""
                    style="width: 14px; height: 14px; margin-right: 4px"
                  />
                  <span>删除</span>
                </div> -->
              </div>
            </div>
          </template>

          <div v-if="loading" class="loading">加载中...</div>
          <div v-if="noMore" class="no-more">已经到底部啦</div>
        </template>

        <div v-else-if="historyList?.length == 0" class="emptyBox">
          <div class="empty-text">暂无对话记录哦~</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable no-unused-vars */
/* eslint-disable no-undef */
import { ref, computed, watch } from "vue";
import { getHistoryById } from "@/api/deepseek.js";
import { getChatHistoryPage, getThemeChatHistoryPage, getThemeChatRecord } from "@/api/aiTeacher.js";

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  currentChatId: {
    type: String,
    default: "",
  },
  aiToken: {
    type: String,
    default: "",
  },
  promptId: {
    type: [Number, String],
    default: null,
  },
});

const emit = defineEmits([
  "close",
  "select",
  "delete",
  "loadMore",
  "toHistory",
]);

const historyList = ref([]);
const selectedId = ref(null);
const loading = ref(false);
const noMore = ref(false);
const pagination = ref({
  page: 1,
  pageSize: 20,
  total: 0,
  totalPages: 0,
});

// 获取历史记录
function getHistorys() {
  if (loading.value) return;

  // 如果没有 aiToken，无法调用接口
  if (!props.aiToken) {
    console.warn("⚠️ aiToken 未提供，无法加载历史记录");
    return;
  }

  loading.value = true;

  // 构建请求参数
  const requestParams = {
    page: String(pagination.value.page), // 接口从 0 开始，所以减 1
    pageSize: String(pagination.value.pageSize),
  };

  // 判断使用哪个接口：promptId 为 17 使用主题对话接口，否则使用口语对话接口
  const isThemeChat = props.promptId == 17;
  
  // 如果不是主题对话且提供了 promptId，添加到参数中
  if (!isThemeChat && props.promptId !== null && props.promptId !== undefined) {
    requestParams.promptId = String(props.promptId);
  }

  // 选择对应的接口
  const apiMethod = isThemeChat ? getThemeChatHistoryPage : getChatHistoryPage;
  
  console.log(`📋 [历史对话] 使用${isThemeChat ? '主题对话' : '口语对话'}接口，promptId:`, props.promptId);

  // 调用真实 API
  apiMethod(requestParams, props.aiToken)
    .then((res) => {
      if (res.code === 200 || res.code === 0) {
        const data = res.data || {};
        const list = data.list || [];

        // 转换数据格式，适配现有代码
        const formattedList = list.map((item) => ({
          id: item.id || "",
          title: item.title || "",
          type: item.title || "", // 兼容 type 字段
          createTime: item.createTime || new Date().toISOString(),
          chatPromptId: item.chatPromptId || "",
          messages: item.messages || [],
        }));

        if (pagination.value.page === 1) {
          historyList.value = formattedList;
        } else {
          historyList.value = [...historyList.value, ...formattedList];
        }

        // 更新分页信息
        pagination.value.total = data.totalCount || 0;
        pagination.value.totalPages = data.totalPages || 0;
        noMore.value = !data.hasNextPage;
        loading.value = false;
      } else {
        loading.value = false;
        console.error("加载历史记录失败:", res.msg || res.message);
      }
    })
    .catch((err) => {
      loading.value = false;
      console.error("加载历史记录失败:", err);
    });
}

// 根据时间分组
const groupedHistory = computed(() => {
  const now = new Date().getTime();
  const groups = {
    今天: [],
    昨天: [],
    "7天内": [],
    "30天内": [],
    一年内: [],
  };

  historyList.value?.forEach((item) => {
    const itemDate = new Date(item.createTime).getTime();
    const diffDays = Math.floor((now - itemDate) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) {
      groups["今天"].push(item);
    } else if (diffDays === 1) {
      groups["昨天"].push(item);
    } else if (diffDays <= 7) {
      groups["7天内"].push(item);
    } else if (diffDays <= 30) {
      groups["30天内"].push(item);
    } else if (diffDays <= 365) {
      groups["一年内"].push(item);
    }
  });

  // 移除空分组
  Object.keys(groups).forEach((key) => {
    if (groups[key].length === 0) {
      delete groups[key];
    }
  });

  return groups;
});

// 处理滚动事件，实现加载更多
const handleScroll = (event) => {
  const target = event.target;
  const scrollTop = target.scrollTop;
  const scrollHeight = target.scrollHeight;
  const clientHeight = target.clientHeight;

  // 距离底部 200px 时触发加载
  if (scrollHeight - scrollTop - clientHeight < 200) {
    handleLoadMore();
  }
};

// 加载更多
const handleLoadMore = () => {
  if (loading.value || noMore.value) return;
  pagination.value.page++;
  getHistorys();
};

// 选择历史对话
const handleSelect = async (item) => {
  selectedId.value = item.id;
  
  // 统一调用主题对话详情接口获取完整的对话记录
  console.log("📋 [历史对话] 获取对话记录详情，chatRecordId:", item.id);
  try {
    const res = await getThemeChatRecord(item.id, props.aiToken);
    if (res.code === 200 || res.code === 0) {
      const chatRecord = res.data || {};
      console.log("✅ [历史对话] 获取对话记录成功:", chatRecord);
      // 触发事件，传递完整的对话记录
      emit("toHistory", chatRecord.messages || [], chatRecord);
    } else {
      console.error("❌ [历史对话] 获取对话记录失败:", res.msg || res.message);
    }
  } catch (err) {
    console.error("❌ [历史对话] 获取对话记录异常:", err);
  }
};

// 删除历史对话
const handleDelete = (item) => {
  if (item.id == selectedId.value) {
    alert("禁止删除当前选中对话");
    return;
  }
  emit("delete", item);
};

// 删除指定ID的记录
function delByID(id) {
  historyList.value = historyList.value.filter((v) => {
    return v.id != id;
  });
}

// 重新加载列表
function reloadList() {
  pagination.value.page = 1;
  noMore.value = false;
  getHistorys();
}

// 重置列表
const resetList = () => {
  pagination.value.page = 1;
  noMore.value = false;
  historyList.value = [];
  getHistorys();
};

const handleClose = () => {
  emit("close");
};

// 监听 visible 变化，打开时加载数据
watch(
  () => props.visible,
  (newVal) => {
    if (newVal) {
      // 弹窗打开时，重置并重新加载数据
      console.log("📋 [历史对话] 弹窗打开，重新加载数据");
      resetList();
    }
  },
  { immediate: true }
);

// 监听 currentChatId 变化
watch(
  () => props.currentChatId,
  (newVal) => {
    selectedId.value = newVal;
  },
  { immediate: true }
);

// 暴露方法给父组件
defineExpose({
  reloadList,
  resetList,
  delByID,
});
</script>

<style scoped lang="scss">
.history-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 0;

  * {
    -webkit-tap-highlight-color: transparent;
    -webkit-touch-callout: none;
  }

  .history-dialog {
    width: 450px;
    height: 100%;
    background: #ffffff;
    box-shadow: -2px 0 8px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    padding: 41px 30px 0 18px;
    box-sizing: border-box;
    position: relative;

    .close-btn {
      position: absolute;
      top: 40px;
      right: 20px;
      width: 20px;
      height: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      z-index: 10;
    }

    .dialog-content {
      flex: 1;
      overflow-y: auto;
      overflow-x: hidden;
      padding: 10px 0;
      box-sizing: border-box;

      .date-title {
        font-size: 12px;
        color: #999;
        padding: 5px 10px;
        margin-top: 10px;

        &:first-child {
          margin-top: 0;
        }

        &.act_line {
          position: relative;
          width: 100%;

          &::before {
            position: absolute;
            top: -1px;
            content: "";
            display: block;
            width: 100%;
            height: 1px;
            background-color: #f2f4fa;
          }
        }
      }

      .history-items {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        padding: 4px;
        box-sizing: border-box;
        overflow-x: hidden;

        .history-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          box-sizing: border-box;
          border-radius: 5px;
          transition: all 0.3s;
          width: 100%;
          max-width: 100%;
          margin-bottom: 4px;
          overflow-x: hidden;

          .item-text {
            flex: 1;
            font-size: 12px;
            color: #333;
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
            padding: 10px 12px;
            line-height: 16px;
            box-sizing: border-box;
            border-radius: 5px;
            min-width: 0;

            &.active {
              background: #f8f5ff;
              color: #a07dff !important;
            }
          }

          .delete-btn {
            display: flex;
            align-items: center;
            margin-left: 15px;
            flex-shrink: 0;
            font-size: 10px;
            color: #9aa1b7;
            cursor: pointer;
            padding: 4px 8px;
            opacity: 0.6;
            transition: opacity 0.2s;

            &:hover {
              opacity: 1;
            }
          }
        }
      }

      .loading,
      .no-more {
        position: relative;
        text-align: center;
        font-size: 10px;
        color: #aaaaaa;
        padding: 20px 0;

        &::before {
          display: block;
          content: "";
          position: absolute;
          left: 52px;
          top: 50%;
          transform: translateY(-50%);
          width: 17px;
          height: 1px;
          background: linear-gradient(
            90deg,
            rgba(187, 187, 187, 0) 0%,
            rgba(187, 187, 187, 0.5) 100%
          );
        }

        &::after {
          display: block;
          content: "";
          position: absolute;
          right: 52px;
          top: 50%;
          transform: translateY(-50%);
          width: 17px;
          height: 1px;
          background: linear-gradient(
            90deg,
            rgba(187, 187, 187, 0.5) 0%,
            rgba(187, 187, 187, 0) 100%
          );
        }
      }
    }

    .emptyBox {
      display: flex;
      align-items: center;
      flex-direction: column;
      color: #a2acce;
      padding: 100px 0 0;

      .empty-text {
        white-space: nowrap;
        font-size: 14px;
      }
    }
  }
}
</style>
